import { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Navbar } from "../components/Navbar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { Lock, Loader2, CheckCircle, AlertTriangle } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token");
  
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      toast.error("Le password non coincidono");
      return;
    }
    
    if (password.length < 6) {
      toast.error("La password deve avere almeno 6 caratteri");
      return;
    }

    setLoading(true);

    try {
      await axios.post(`${API}/auth/reset-password`, {
        token,
        new_password: password
      });
      setSuccess(true);
      toast.success("Password reimpostata con successo!");
    } catch (error) {
      const message = error.response?.data?.detail || "Errore nel reset della password";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  // No token provided
  if (!token) {
    return (
      <div className="min-h-screen bg-[#F8FAFC]" data-testid="reset-password-page">
        <Navbar />
        <div className="flex items-center justify-center py-12 px-4">
          <div className="w-full max-w-md">
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-8 text-center">
              <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <AlertTriangle className="w-8 h-8 text-amber-600" />
              </div>
              <h1 className="text-2xl font-bold text-slate-900 mb-2">Link non valido</h1>
              <p className="text-slate-600 mb-6">
                Il link per reimpostare la password non è valido o è scaduto.
              </p>
              <Link to="/forgot-password">
                <Button className="bg-[#DA291C] hover:bg-[#B91C1C] text-white">
                  Richiedi nuovo link
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="reset-password-page">
      <Navbar />
      
      <div className="flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="w-full max-w-md animate-fadeIn">
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-8">
            {success ? (
              <div className="text-center">
                <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-8 h-8 text-emerald-600" />
                </div>
                <h1 className="text-2xl font-bold text-slate-900 mb-2">Password reimpostata!</h1>
                <p className="text-slate-600 mb-6">
                  La tua password è stata aggiornata con successo. Ora puoi accedere con la nuova password.
                </p>
                <Button 
                  onClick={() => navigate("/login")}
                  className="bg-[#DA291C] hover:bg-[#B91C1C] text-white"
                >
                  Vai al login
                </Button>
              </div>
            ) : (
              <>
                <div className="text-center mb-8">
                  <h1 className="text-2xl font-bold text-slate-900">Reimposta password</h1>
                  <p className="text-slate-600 mt-2">
                    Inserisci la tua nuova password.
                  </p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-5" data-testid="reset-password-form">
                  <div>
                    <Label htmlFor="password" className="text-slate-700">Nuova password</Label>
                    <div className="relative mt-1.5">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                      <Input
                        id="password"
                        name="password"
                        type="password"
                        required
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="••••••••"
                        className="pl-10"
                        data-testid="reset-password"
                        minLength={6}
                      />
                    </div>
                    <p className="text-xs text-slate-500 mt-1">Minimo 6 caratteri</p>
                  </div>

                  <div>
                    <Label htmlFor="confirmPassword" className="text-slate-700">Conferma password</Label>
                    <div className="relative mt-1.5">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                      <Input
                        id="confirmPassword"
                        name="confirmPassword"
                        type="password"
                        required
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        placeholder="••••••••"
                        className="pl-10"
                        data-testid="reset-confirm-password"
                      />
                    </div>
                  </div>

                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-[#DA291C] hover:bg-[#B91C1C] text-white h-11 font-semibold"
                    data-testid="reset-submit"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Salvataggio...
                      </>
                    ) : (
                      "Reimposta password"
                    )}
                  </Button>
                </form>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
